// Trade-readiness presentation layer — pure, frontend-only derivation of the
// three sequential signal dimensions a premium-selling desk evaluates in order:
//   1. Regime Health   (is the regime favourable AND fresh enough to lean on?)
//   2. Strike Alignment (is the screener strike at the backtest-optimal delta
//                        for THIS regime? — the single most actionable signal)
//   3. Sizing Confidence(how much size does the evidence + execution gate support?)
//
// Everything here is a GAUGE (0-100), never a hard block. It reads ONLY fields
// already returned by /screener/model + the embedded execution_verdict — no API
// or engine changes, no mutation of execution_matrix computed values.

// Mirror of backend services/strike_engine.REGIME_DELTA_TARGETS (short leg).
export const REGIME_DELTA_TARGET = {
  "Quiet Range": 0.28,
  "Bullish Trend": 0.22,
  "Bearish Trend": 0.14,
  "Volatile": 0.12,
};

export function regimeDeltaTarget(regime) {
  return REGIME_DELTA_TARGET[regime] ?? 0.25;
}

const clamp01 = (x) => Math.max(0, Math.min(1, x));

// Green ≥67, Amber ≥40, Red below — "red = attention required", not forbidden.
export function signalColor(v) {
  if (v == null) return "var(--hoip-text-tertiary)";
  if (v >= 67) return "#10B981";
  if (v >= 40) return "#F59E0B";
  return "#EF4444";
}

export function signalTone(v) {
  if (v == null) return "muted";
  if (v >= 67) return "good";
  if (v >= 40) return "watch";
  return "attention";
}

// φ → freshness reading.
export function freshnessLabel(phi) {
  if (phi == null) return null;
  if (phi <= 1.0) return { label: "fresh", color: "#10B981" };
  if (phi <= 1.5) return { label: "aging", color: "#F59E0B" };
  if (phi <= 2.0) return { label: "stretched", color: "#F59E0B" };
  return { label: "overextended", color: "#EF4444" };
}

export function computeTradeSignals(r, strategy) {
  const ev = (strategy === "cc" ? r.cc_execution_verdict : r.execution_verdict) || {};
  const phi = ev.phi ?? (r.regime_age_bars && r.median_sojourn_days
    ? +(r.regime_age_bars / r.median_sojourn_days).toFixed(2) : null);

  // ── 1. Regime Health ──────────────────────────────────────
  // regime_fit already blends regime favourability × posterior × flip risk.
  // We additionally discount it by regime freshness (φ): an overextended
  // regime is statistically "due" to transition, so its health degrades.
  const regimeFit = r.regime_fit ?? null;
  const freshnessFactor = phi == null ? 1 : (phi <= 1 ? 1 : Math.max(0.2, 1.5 - 0.5 * phi));
  const regimeHealth = regimeFit == null ? null
    : Math.round(clamp01(regimeFit * freshnessFactor) * 100);

  // ── 2. Strike Alignment (delta gap) ───────────────────────
  const deltaCurrent = r._delta ?? null;
  const deltaTarget = r.regime_delta_target ?? regimeDeltaTarget(r.regime);
  let strikeAlignment = null, deltaGap = null, deltaAction = null, deltaDir = null;
  if (deltaCurrent != null && deltaTarget != null) {
    deltaGap = +(deltaCurrent - deltaTarget).toFixed(2);
    // A 0.20 delta gap (~2 strikes for a typical name) maps to zero alignment.
    strikeAlignment = Math.round(clamp01(1 - Math.abs(deltaGap) / 0.20) * 100);
    if (deltaGap > 0.02) { deltaDir = "tighten"; deltaAction = `Above regime-optimal Δ${deltaTarget.toFixed(2)}`; }
    else if (deltaGap < -0.02) { deltaDir = "loosen"; deltaAction = `Below regime-optimal Δ${deltaTarget.toFixed(2)}`; }
    else { deltaDir = "aligned"; deltaAction = `At regime-optimal Δ${deltaTarget.toFixed(2)}`; }
  }

  // ── 3. Sizing Confidence ──────────────────────────────────
  const sizeMult = ev.size_multiplier ?? null;
  const btQ = r.backtest_quality ?? null;
  const pnt = r._pnt ?? null;
  const pntNorm = pnt == null ? 0.5 : clamp01((pnt - 50) / 45);
  let sizingConfidence = null;
  if (sizeMult != null || btQ != null) {
    sizingConfidence = Math.round(
      clamp01(0.5 * (sizeMult ?? 0.5) + 0.3 * (btQ ?? 0.4) + 0.2 * pntNorm) * 100
    );
  }
  const recommendedSizePct = sizeMult != null ? Math.round(sizeMult * 100) : null;

  return {
    phi,
    regimeHealth,
    strikeAlignment,
    deltaCurrent,
    deltaTarget,
    deltaGap,
    deltaAction,
    deltaDir,
    sizingConfidence,
    recommendedSizePct,
    verdict: ev.verdict || null,
  };
}

// Subtle context tint (Principle 6 — quality is background, not a gate).
export function qualityTint(label) {
  switch (label) {
    case "Strong": return "rgba(16,185,129,0.05)";
    case "Moderate": return "rgba(59,130,246,0.04)";
    case "Weak":
    case "Small-cap": return "rgba(245,158,11,0.05)";
    default: return undefined;
  }
}
